import { ROUTE_PATHS } from '@constants/routes';
import { useLocalization } from '@lib/localization';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton, { type ListItemButtonProps } from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Stack from '@mui/material/Stack';
import { styled } from '@mui/material/styles';
import { usePermission } from '@store/hooks';
import {
  CogIcon, HomeIcon, MapMarkerIcon, ProjectDiagramIcon, ServerIcon, SlidersHIcon, UserFriendsIcon, UsersIcon,
} from 'react-line-awesome';
import { Fragment } from 'react';
import { Link, useLocation, type LinkProps } from 'react-router-dom';

const ListItemButtonLink = styled(ListItemButton)<ListItemButtonProps & LinkProps>(() => ({
  padding: '2px !important',
  height: '38px',
}));

const StyledStack = styled(Stack)({
  flexGrow: 1,
  padding: '8px',
  justifyContent: 'space-between',
});

const StyledList = styled(List)({
  gap: '0.375rem',
  padding: 0,
});

const listItemIconStyles = {
  marginLeft: '6px',
  marginRight: '6px',
  width: '28px',
  height: '28px',
  backgroundColor: '#26272F',
  borderRadius: '.375rem',
  marginInlineEnd: '.325rem',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  border: '1px solid rgb(54, 56, 67)',
  '& i': {
    fontSize: '18px',
    color: '#B5B7C8'
  },
};

const secondaryListItemIconStyles = {
  marginLeft: '6px',
  marginRight: '6px',
  width: '28px',
  height: '28px',
  marginInlineEnd: '.325rem',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  '& i': {
    fontSize: '18px',
    color: '#B5B7C8'
  },
};

const subListStyles = {
  ml: '20px',
  pl: '14px',
  borderLeft: '1px solid',
  borderColor: 'divider',
  gap: '2px',
  py: '2px',
};

const subItemStyles = {
  height: '32px',
  '& .MuiListItemText-primary': { fontSize: 13.5 },
};

interface NavItem {
  text: string;
  icon: React.ReactNode;
  url: string;
  /** Sibling sections that belong to the same entry, e.g. search results under the tree. */
  alsoMatch?: string[];
  /**
   * Pages that live under this entry. A parent with children is a section header rather
   * than a destination of its own: it points at its first child, and it reads as active
   * whenever any child does.
   */
  children?: NavItem[];
}

const MenuContent: React.FC<{ toggleDrawer?: () => void }> = ({ toggleDrawer }) => {
  const { can, canInAnyTeam } = usePermission();

  const { t } = useLocalization();
  const location = useLocation();
  const currentPath = location.pathname;

  // The menu marks the section you are in, not the page: a folder, a project inside it or a
  // search result all belong to the same entry. Home is matched exactly — as a prefix it
  // would own every route in the app.
  const isSectionActive = (url: string, alsoMatch: string[] = []): boolean =>
    [url, ...alsoMatch].some((section) => section === ROUTE_PATHS.HOME
      ? currentPath === section
      : currentPath === section || currentPath.startsWith(`${section}/`));

  const mainListItems: NavItem[] = [
    { text: t('Common_Home'), icon: <HomeIcon />, url: ROUTE_PATHS.HOME },
    // Search results live under /projects while the tree itself is under /folders.
    { text: t('Common_Projects'), icon: <ProjectDiagramIcon />, url: ROUTE_PATHS.FOLDERS, alsoMatch: [ROUTE_PATHS.PROJECTS] },
    ...(canInAnyTeam('team:get')
      ? [{ text: t('Common_Teams'), icon: <UserFriendsIcon />, url: ROUTE_PATHS.TEAMS }]
      : []
    ),
    ...(can('user:get')
      ? [{ text: t('Common_Users'), icon: <UsersIcon />, url: ROUTE_PATHS.USERS }]
      : []
    ),
    // Gated on reading, not authoring: every team role carries `poi_type:get`, and an
    // INSPECTOR or ANALYST has reason to look up what a type asks for. The page itself is
    // already read-only for them — the create button needs `poi_type:post`, the locale
    // button `server_config:put`, and every control on the detail page is gated on that
    // type's own `myPermissions`. Report customization joins this list once it exists.
    ...(canInAnyTeam('poi_type:get')
      ? [{
        text: t('Customization_Section'),
        icon: <SlidersHIcon />,
        url: ROUTE_PATHS.CUSTOMIZATIONS_POI_TYPES,
        children: [
          {
            text: t('Customization_CustomPoi'),
            icon: <MapMarkerIcon />,
            url: ROUTE_PATHS.CUSTOMIZATIONS_POI_TYPES,
          },
        ],
      }]
      : []
    ),
  ];

  const secondaryListItems: NavItem[] = [
    ...(can('server_config:get')
      ? [{ text: t('Common_ServerConfig'), icon: <ServerIcon />, url: ROUTE_PATHS.SERVER_CONFIG }]
      : []
    ),
    { text: t('Common_Settings'), icon: <CogIcon />, url: ROUTE_PATHS.SETTINGS },
  ].filter(Boolean);

  return (
    <StyledStack>
      <StyledList dense>
        {mainListItems.map((item, index) => (
          <Fragment key={index}>
            <ListItem disablePadding>
              <ListItemButtonLink
                component={Link}
                to={item.url}
                selected={isSectionActive(item.url, item.alsoMatch)}
                onClick={toggleDrawer}
              >
                <ListItemIcon sx={listItemIconStyles}>
                  {item.icon}
                </ListItemIcon>
                <ListItemText primary={item.text} />
              </ListItemButtonLink>
            </ListItem>
            {item.children && (
              <StyledList dense sx={subListStyles}>
                {item.children.map((child, childIndex) => (
                  <ListItem key={childIndex} disablePadding>
                    <ListItemButtonLink
                      component={Link}
                      to={child.url}
                      selected={isSectionActive(child.url, child.alsoMatch)}
                      onClick={toggleDrawer}
                      sx={subItemStyles}
                    >
                      <ListItemIcon sx={secondaryListItemIconStyles}>
                        {child.icon}
                      </ListItemIcon>
                      <ListItemText primary={child.text} />
                    </ListItemButtonLink>
                  </ListItem>
                ))}
              </StyledList>
            )}
          </Fragment>
        ))}
      </StyledList>
      <StyledList dense sx={{ gap: '.1rem' }}>
        {secondaryListItems.map((item, index) => (
          <ListItem key={index} disablePadding>
            <ListItemButtonLink
              component={Link}
              to={item.url}
              selected={isSectionActive(item.url, item.alsoMatch)}
              onClick={toggleDrawer}
            >
              <ListItemIcon sx={secondaryListItemIconStyles}>
                {item.icon}
              </ListItemIcon>
              <ListItemText primary={item.text} />
            </ListItemButtonLink>
          </ListItem>
        ))}
      </StyledList>
    </StyledStack>
  );
};

export default MenuContent;